import React, { useCallback, useState, useEffect, useRef } from 'react';
import type { FocusEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import Lozenge from '@atlaskit/lozenge/new';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import type { RawAction } from './__types__/SpaceSuggestion.ts';

/**
 * Data shape rendered by a SuggestionCard.
 *
 * NOTE: this type no longer carries an `iconUrl` field — the card now renders
 * a fixed `AiGenerativeCleanupIcon` for every suggestion. Per-suggestion icons
 * can be re-introduced by extending the type once the design supports it.
 */
export type SuggestionItem = {
	summary: string;
	/**
	 * The rendered timestamp content. Accepts a plain string or any ReactNode
	 * so callers can pass a live `<TimeAgo />` element directly.
	 */
	timestamp: ReactNode;
	/** Actions associated with this suggestion, forwarded to the review panel. */
	actions?: RawAction[];
};

export type ActivityItem = SuggestionItem & {
	status: ActivityStatus;
};

type ActivityStatus = 'success' | 'failed';

type SuggestionCardProps =
	| {
			item: SuggestionItem;
			activity?: false;
			status?: never;
			onReview: (item: SuggestionItem) => void;
			onApprove?: () => void;
			onReject?: () => void;
			isSelected?: boolean;
	  }
	| {
			item: ActivityItem;
			activity: true;
			status: ActivityStatus;
			onReview?: never;
			onApprove?: never;
			onReject?: never;
			isSelected?: never;
	  };

export function SuggestionCard(props: SuggestionCardProps) {
	const { item } = props;
	const { formatMessage } = useIntl();
	const cardRef = useRef<HTMLDivElement>(null);
	const isSelected = !props.activity && Boolean(props.isSelected);

	const handleReview = useStableCallback(() => {
		if (!props.activity) {
			props.onReview(item);
		}
	});

	useEffect(() => {
		if (isSelected) {
			cardRef.current?.focus();
		}
	}, [isSelected]);

	// Track hover and keyboard focus in React state. We tried pure-CSS
	// hover-reveal via `&:hover` nested rules in `cssMap` (both `@atlaskit/css`
	// and `@compiled/react`) and via parent → child CSS custom properties; none
	// produced the expected runtime hover behaviour in this codebase. The
	// React-state approach is bulletproof and trivially testable.
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	// We use `onMouseOver` / `onMouseOut` rather than `onMouseEnter` /
	// `onMouseLeave` because React's synthetic `onMouseEnter` / `onMouseLeave`
	// don't fire reliably under `@testing-library/react`'s `fireEvent`
	// (`mouseenter` does not bubble, and React skips dispatch in some cases).
	// `onMouseOver` / `onMouseOut` bubble and behave identically for our
	// purposes since we don't care about transitions between child elements.
	const handleMouseOver = useCallback(() => setIsHovered(true), []);
	const handleMouseOut = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
		// Only collapse when the pointer is leaving the entire card subtree
		// (not just moving between children).
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setIsHovered(false);
		}
	}, []);

	const handleFocus = useCallback(() => setIsFocused(true), []);
	const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
		// `onBlur` bubbles from any child element receiving focus; only collapse
		// when focus is leaving the entire card subtree.
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setIsFocused(false);
		}
	}, []);

	const isInteractive = !props.activity;
	const showActions = isInteractive && (isHovered || isFocused);

	return (
		<div
			ref={cardRef}
			data-testid={isInteractive ? 'space-suggestion-card' : undefined}
			data-selected={isInteractive ? (isSelected ? 'true' : 'false') : undefined}
			tabIndex={isInteractive ? -1 : undefined}
			css={[styles.card, isSelected && styles.selectedCard]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- The hover background is toggled by React state; cssMap requires statically-resolvable class selection, so we use an inline style override instead.
			style={
				isInteractive && isHovered && !isSelected
					? { backgroundColor: token('elevation.surface.overlay.hovered') }
					: undefined
			}
			onMouseOver={isInteractive ? handleMouseOver : undefined}
			onMouseOut={isInteractive ? handleMouseOut : undefined}
			onFocus={isInteractive ? handleFocus : undefined}
			onBlur={isInteractive ? handleBlur : undefined}
		>
			<Inline alignBlock="center" space="space.150" spread="space-between">
				<Inline alignBlock="start" space="space.150" grow="hug">
					<div css={styles.iconWrapper}>
						<AiGenerativeCleanupIcon label="" color={token('color.icon')} spacing="spacious" />
					</div>
					<Stack space="space.050">
						<Text>{item.summary}</Text>
						<Text size="small" color="color.text.subtlest">
							{item.timestamp}
						</Text>
					</Stack>
				</Inline>
				{props.activity ? (
					<ActivityStatusLozenge status={props.status} />
				) : (
					<div
						css={styles.actions}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Hover/focus-reveal opacity is toggled by React state; cssMap classes can't be swapped dynamically, so we use an inline style override.
						style={showActions ? { opacity: 1, pointerEvents: 'auto' } : undefined}
						data-card-actions
					>
						<Inline space="space.075" alignBlock="center">
							<IconButton
								icon={CheckMarkIcon}
								label={formatMessage(i18n.approveLabel)}
								appearance="subtle"
								onClick={props.onApprove}
								isTooltipDisabled={false}
							/>
							<IconButton
								icon={CrossIcon}
								label={formatMessage(i18n.rejectLabel)}
								appearance="subtle"
								onClick={props.onReject}
								isTooltipDisabled={false}
							/>
							<Button
								onClick={handleReview}
								testId="space-suggestion-review-button"
								data-selected={isSelected ? 'true' : 'false'}
							>
								{formatMessage(i18n.reviewButton)}
							</Button>
						</Inline>
					</div>
				)}
			</Inline>
		</div>
	);
}

function ActivityStatusLozenge({ status }: { status: ActivityStatus }) {
	const { formatMessage } = useIntl();

	if (status === 'success') {
		return (
			<Lozenge appearance="success" iconBefore={StatusSuccessIcon}>
				{formatMessage(i18n.statusSuccess)}
			</Lozenge>
		);
	}

	return (
		<Lozenge appearance="warning" iconBefore={StatusWarningIcon}>
			{formatMessage(i18n.statusFailed)}
		</Lozenge>
	);
}

const ICON_WRAPPER_SIZE = 38;

const styles = cssMap({
	card: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		outlineStyle: 'none',
		transition: 'background-color 120ms ease-out',
	},
	selectedCard: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.selected'),
		'&:focus': {
			outlineWidth: token('border.width.selected'),
			outlineStyle: 'solid',
			outlineColor: token('color.border.selected'),
		},
	},
	iconWrapper: {
		width: `${ICON_WRAPPER_SIZE}px`,
		height: `${ICON_WRAPPER_SIZE}px`,
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		backgroundColor: token('color.background.neutral'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	actions: {
		opacity: 0,
		pointerEvents: 'none',
		transition: 'opacity 120ms ease-out',
	},
});

const i18n = defineMessages({
	reviewButton: {
		id: 'space-suggestions.suggestion-card.review-button',
		defaultMessage: 'Review',
		description:
			'Label for the button that opens the right-rail review panel for a suggestion that needs review',
	},
	approveLabel: {
		id: 'space-suggestions.suggestion-card.approve-label',
		defaultMessage: 'Approve',
		description:
			'Accessible label and tooltip for the icon button that approves a suggestion directly from the card',
	},
	rejectLabel: {
		id: 'space-suggestions.suggestion-card.reject-label',
		defaultMessage: 'Reject',
		description:
			'Accessible label and tooltip for the icon button that rejects a suggestion directly from the card',
	},
	statusSuccess: {
		id: 'space-suggestions.suggestion-card.status-success',
		defaultMessage: 'Success',
		description:
			'Lozenge label shown on an activity card when the agent action completed successfully',
	},
	statusFailed: {
		id: 'space-suggestions.suggestion-card.status-failed',
		defaultMessage: 'Failed',
		description: 'Lozenge label shown on an activity card when the agent action failed',
	},
});
